import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'

import {
  CompanyResponse,
  MentionResponse,
  MentionsStatsResponse,
  Paged,
  PolarityValue,
  SearchResponse,
  ExportMentionsResponse,
  KeyMessageResponse,
  SetMentionReviewedRequest,
  UpdateMentionRequest,
} from '@shared/models'

import { MultiSelectionBinder, SelectionBinder, Selector } from '@/components'

import { Routes } from '@/configs'
import { InputBinder, useInput, useRouting, useTimeInput } from '@/hooks'
import { Alert, Api, ApiRunFunc, Dialog } from '@/modules'
import { Str } from '@/utils/strings'

import { getMonthDays } from '@/utils/date'
import { format, isValid, parse } from 'date-fns'
import DialogTitle from '@mui/material/DialogTitle'

export const DELETE_ALL_MAX_MENTIONS = 5000

// Compartilhado entre o painel de filtros e a restauração do localStorage, pra
// restaurar a opção com o texto certo (o hasChanges restaura `{ text: 'COM' }`
// e o seletor mostra "COM" depois do F5).
export const REVIEWED_OPTIONS = [
  // O `value` vai gravado no filtro salvo (FilterConfig.reviewed); mudar só o
  // rótulo mantém as configurações que já existem funcionando.
  { text: 'Revisadas', value: 'COM' },
  { text: 'Não revisadas', value: 'SEM' },
]

export type DefaultFilters = {
  startDate: string
  endDate: string
  brands: SearchResponse[]
  themeGroups: SearchResponse[]
}

export type Image = {
  id: string
  mime: string
  content: string
}

type CurrMention = {
  idx: number
  val: MentionResponse
}

type ChannelsSearchResponse = SearchResponse & {
  source?: number
}

const createBaseFilters = (
  selectedBrandItems: SearchResponse[],
  selectedThemeGroups: SearchResponse[],
  startDate: string,
  endDate: string,
  currentPage?: number,
  pageSize?: number,
  orderBy?: string,
) => {
  const filters: {
    startDate: string
    endDate: string
    brandIDs: (string | number)[]
    themeGroupInternalIDs?: (string | number)[]
    page?: number
    size?: number
    orderBy?: string
  } = {
    startDate,
    endDate,
    brandIDs: selectedBrandItems.map((b) => b.value),
    ...(currentPage !== undefined && { page: currentPage }),
    ...(pageSize !== undefined && { size: pageSize }),
    ...(orderBy && { orderBy }),
  }
  if (selectedThemeGroups.length > 0) {
    filters.themeGroupInternalIDs = selectedThemeGroups.map((b) => b.value)
  }

  return filters
}

const addFilters = (
  baseFilters: ReturnType<typeof createBaseFilters>,
  options: {
    selectedApproaches: SearchResponse[]
    selectedChannels: SearchResponse[]
    selectedPolarities: SearchResponse[]
    selectedProfiles: SearchResponse[]
    selectedPublishers: SearchResponse[]
    selectedRoles: SearchResponse[]
    selectedKeyMessages: SearchResponse[]
    selectedTiers: SearchResponse[]
    selectedSources: SearchResponse[]
    selectedSpokespersons: SearchResponse[]
    selectedThemes: SearchResponse[]
    selectedThemeGroups: SearchResponse[]
    excludedApproaches?: SearchResponse[]
    excludedChannels?: SearchResponse[]
    excludedPolarities?: SearchResponse[]
    excludedProfiles?: SearchResponse[]
    excludedPublishers?: SearchResponse[]
    excludedRoles?: SearchResponse[]
    excludedKeyMessages?: SearchResponse[]
    excludedTiers?: SearchResponse[]
    excludedSources?: SearchResponse[]
    excludedSpokespersons?: SearchResponse[]
    excludedThemes?: SearchResponse[]
    excludedThemeGroups?: SearchResponse[]
    content?: string
    startTime?: string
    endTime?: string
    engagementType?: string
    engagementMin?: string
    engagementMax?: string
    hasChanges?: boolean
    hasContentIssues?: boolean
    reviewed?: boolean
  },
) => {
  const filters: Record<string, any> = { ...baseFilters }

  if (options.selectedPolarities.length > 0) {
    filters.polarities = options.selectedPolarities.map((p) => p.value as string)
  }
  if (options.excludedPolarities?.length && options.excludedPolarities.length > 0) {
    filters.excludedPolarities = options.excludedPolarities.map((p) => p.value as string)
  }
  if (options.selectedChannels.length > 0) {
    filters.channelInternalIDs = options.selectedChannels.map((c) => Number(c.value))
  }
  if (options.excludedChannels?.length && options.excludedChannels.length > 0) {
    filters.excludedChannelInternalIDs = options.excludedChannels.map((c) => Number(c.value))
  }
  if (options.selectedRoles.length > 0) {
    filters.roleInternalIDs = options.selectedRoles.map((r) => Number(r.value))
  }
  if (options.excludedRoles?.length && options.excludedRoles.length > 0) {
    filters.excludedRoleInternalIDs = options.excludedRoles.map((r) => Number(r.value))
  }
  if (options.selectedKeyMessages.length > 0) {
    filters.keyMessageInternalIDs = options.selectedKeyMessages.map((k) => k.value as string)
  }
  if (options.excludedKeyMessages?.length && options.excludedKeyMessages.length > 0) {
    filters.excludedKeyMessageInternalIDs = options.excludedKeyMessages.map((k) => k.value as string)
  }
  if (options.selectedTiers.length > 0) {
    filters.tiers = options.selectedTiers.map((p) => p.text as string)
  }
  if (options.excludedTiers?.length && options.excludedTiers.length > 0) {
    filters.excludedTiers = options.excludedTiers.map((p) => p.text as string)
  }
  if (options.selectedApproaches.length > 0) {
    filters.approachInternalIDs = options.selectedApproaches.map((a) => Number(a.value))
  }
  if (options.excludedApproaches?.length && options.excludedApproaches.length > 0) {
    filters.excludedApproachInternalIDs = options.excludedApproaches.map((a) => Number(a.value))
  }
  if (options.selectedThemes.length > 0) {
    filters.themeInternalIDs = options.selectedThemes.flatMap((t) => String(t.value).split(','))
  }
  if (options.excludedThemes?.length && options.excludedThemes.length > 0) {
    filters.excludedThemeInternalIDs = options.excludedThemes.flatMap((t) => String(t.value).split(','))
  }
  if (options.selectedPublishers.length > 0) {
    filters.publisherInternalIDs = options.selectedPublishers.map((p) => Number(p.value))
  }
  if (options.excludedPublishers?.length && options.excludedPublishers.length > 0) {
    filters.excludedPublisherInternalIDs = options.excludedPublishers.map((p) => Number(p.value))
  }
  if (options.selectedSources.length > 0) {
    filters.sourceInternalIDs = options.selectedSources.map((s) => Number(s.value))
  }
  if (options.excludedSources?.length && options.excludedSources.length > 0) {
    filters.excludedSourceInternalIDs = options.excludedSources.map((s) => Number(s.value))
  }
  if (options.selectedSpokespersons.length > 0) {
    filters.spokespersonInternalIDs = options.selectedSpokespersons.map((s) => String(s.value))
  }
  if (options.excludedSpokespersons?.length && options.excludedSpokespersons.length > 0) {
    filters.excludedSpokespersonInternalIDs = options.excludedSpokespersons.map((s) => String(s.value))
  }
  if (options.selectedProfiles.length > 0) {
    filters.profileNames = options.selectedProfiles.map((p) => p.value as string)
  }
  if (options.excludedProfiles?.length && options.excludedProfiles.length > 0) {
    filters.excludedProfileNames = options.excludedProfiles.map((p) => p.value as string)
  }
  if (options.selectedThemeGroups.length > 0) {
    filters.themeGroupInternalIDs = options.selectedThemeGroups.map((tg) => tg.value)
  }
  if (options.excludedThemeGroups?.length && options.excludedThemeGroups.length > 0) {
    filters.excludedThemeGroupInternalIDs = options.excludedThemeGroups.map((tg) => tg.value)
  }
  if (options.content) {
    filters.content = options.content
  }
  if (options.startTime) {
    filters.startTime = options.startTime
  }
  if (options.endTime) {
    filters.endTime = options.endTime
  }
  if (options.engagementType && (options.engagementMin || options.engagementMax)) {
    filters.engagementType = options.engagementType
    if (options.engagementMin) {
      filters.engagementMin = Number(options.engagementMin)
    }
    if (options.engagementMax) {
      filters.engagementMax = Number(options.engagementMax)
    }
  }
  if (options.hasChanges !== undefined) {
    filters.hasChanges = options.hasChanges
  }
  if (options.hasContentIssues !== undefined) {
    filters.hasContentIssues = options.hasContentIssues
  }
  if (options.reviewed !== undefined) {
    filters.reviewed = options.reviewed
  }

  return filters
}

export type MentionModalType = 'edit' | 'detail' | null

// Tag removível direto da lista de menções, sem abrir o modal de edição.
// O tema carrega o grupo junto porque a readição (desfazer) exige os dois.
export type RemovableMentionTag =
  | { kind: 'theme'; internalID: string; name: string; themeGroupInternalID: string }
  | { kind: 'keyMessage'; internalID: string; name: string }
  | { kind: 'spokesperson'; internalID: string; name: string }

const TAG_COPY: Record<RemovableMentionTag['kind'], { removed: string; restored: string; error: string }> = {
  theme: { removed: 'Tema removido', restored: 'Tema restaurado', error: 'Erro ao remover tema' },
  keyMessage: {
    removed: 'Mensagem-chave removida',
    restored: 'Mensagem-chave restaurada',
    error: 'Erro ao remover mensagem-chave',
  },
  spokesperson: { removed: 'Porta-voz removido', restored: 'Porta-voz restaurado', error: 'Erro ao remover porta-voz' },
}

// Janela do "Desfazer". O padrão do alerta (4,5s) é curto demais pra dar tempo
// de ler e decidir. Não passar `{}` sem ttl aqui: nesse caso o alerta nunca
// fecha sozinho (ver alert.context.tsx).
const UNDO_ALERT_TTL = 10000

function mentionTagKey(mention: MentionResponse, tag: RemovableMentionTag): string {
  return `${mention.postID}_${mention.brandID}_${tag.kind}_${tag.internalID}`
}

function buildTagDeletion(mention: MentionResponse, tag: RemovableMentionTag): UpdateMentionRequest {
  switch (tag.kind) {
    case 'theme':
      return { brandID: mention.brandID, themeDeletions: [{ themeInternalID: tag.internalID }] }
    case 'keyMessage':
      return { brandID: mention.brandID, keyMessageDeletions: [{ keyMessageInternalID: tag.internalID }] }
    case 'spokesperson':
      return { brandID: mention.brandID, spokespersonDeletions: [{ spokespersonInternalID: tag.internalID }] }
  }
}

function buildTagAddition(mention: MentionResponse, tag: RemovableMentionTag): UpdateMentionRequest {
  switch (tag.kind) {
    case 'theme':
      return {
        brandID: mention.brandID,
        themeAdditions: [{ themeGroupInternalID: tag.themeGroupInternalID, themeInternalID: tag.internalID }],
      }
    case 'keyMessage':
      return { brandID: mention.brandID, keyMessageAdditions: [{ keyMessageInternalID: tag.internalID }] }
    case 'spokesperson':
      return { brandID: mention.brandID, spokespersonAdditions: [{ spokespersonInternalID: tag.internalID }] }
  }
}

function withoutTag(mention: MentionResponse, tag: RemovableMentionTag): Partial<MentionResponse> {
  switch (tag.kind) {
    case 'theme':
      return {
        themeGroup: (mention.themeGroup ?? [])
          .map((group) => ({ ...group, themes: group.themes.filter((theme) => theme.internalID !== tag.internalID) }))
          .filter((group) => group.themes.length > 0),
      }
    case 'keyMessage':
      return { keyMessages: (mention.keyMessages ?? []).filter((km) => km.internalID !== tag.internalID) }
    case 'spokesperson':
      return { spokespersons: (mention.spokespersons ?? []).filter((sp) => sp.internalID !== tag.internalID) }
  }
}

// Restaura a partir do snapshot tirado antes da remoção, em vez de remontar a
// tag na mão — no caso do tema isso evita ter que recriar o grupo quando ele
// ficou vazio e foi descartado do render.
function withTagRestored(snapshot: MentionResponse, tag: RemovableMentionTag): Partial<MentionResponse> {
  switch (tag.kind) {
    case 'theme':
      return { themeGroup: snapshot.themeGroup }
    case 'keyMessage':
      return { keyMessages: snapshot.keyMessages }
    case 'spokesperson':
      return { spokespersons: snapshot.spokespersons }
  }
}

interface MentionsContextOutput {
  defaultFilters?: DefaultFilters
  currentMention?: CurrMention
  mentionModalType: MentionModalType
  currentPageSize: number
  currentSort: { key: string; direction: 'ASC' | 'DESC' }
  setCurrentSort: (sort: { key: string; direction: 'ASC' | 'DESC' }) => void
  setCurrentPageSize: (size: number) => void
  selectMention: (mention?: CurrMention, modalType?: 'edit' | 'detail') => void

  applyFilters: () => void
  resetFilters: () => void
  downloadSpreadsheet: () => Promise<ExportMentionsResponse>

  contentBinder: InputBinder
  endDateBinder: DateBinder
  endTimeBinder: InputBinder
  engagementMaxBinder: InputBinder
  engagementMinBinder: InputBinder
  engagementTypeBinder: SelectionBinder
  hasChangesBinder: SelectionBinder
  hasContentIssuesBinder: SelectionBinder
  reviewedBinder: SelectionBinder
  startDateBinder: DateBinder
  startTimeBinder: InputBinder

  approachBinder: MultiSelectionBinder
  brandBinder: MultiSelectionBinder
  channelBinder: MultiSelectionBinder
  polarityBinder: MultiSelectionBinder
  profileBinder: MultiSelectionBinder
  publisherBinder: MultiSelectionBinder
  roleBinder: MultiSelectionBinder
  keyMessageBinder: MultiSelectionBinder
  tierBinder: MultiSelectionBinder
  sourceBinder: MultiSelectionBinder
  spokespersonBinder: MultiSelectionBinder
  themeBinder: MultiSelectionBinder
  themeGroupBinder: MultiSelectionBinder

  isExporting: boolean
  isLoadingMentions: boolean
  isLoadingStats: boolean
  isSearchingApproaches: boolean
  isSearchingBrands: boolean
  isSearchingChannels: boolean
  isSearchingProfiles: boolean
  isSearchingPublishers: boolean
  isSearchingRoles: boolean
  isSearchingKeyMessages: boolean
  isSearchingTiers: boolean
  isSearchingSpokespersons: boolean
  isSearchingThemeGroups: boolean
  isSearchingThemes: boolean

  approaches?: SearchResponse[]
  brands?: SearchResponse[]
  channels?: ChannelsSearchResponse[]
  images: Map<string, string>
  mentions?: Paged<MentionResponse>
  stats?: MentionsStatsResponse
  polarities?: SearchResponse[]
  profiles?: SearchResponse[]
  publishers?: SearchResponse[]
  roles?: SearchResponse[]
  keyMessages?: KeyMessageResponse[]
  tiers?: SearchResponse[]
  spokespersons?: SearchResponse[]
  themeGroups?: SearchResponse[]
  themes?: SearchResponse[]

  loadMentions: (requestBody: any) => void
  searchApproaches: ApiRunFunc
  searchBrands: ApiRunFunc
  searchChannels: ApiRunFunc
  searchProfiles: ApiRunFunc
  searchPublishers: ApiRunFunc
  searchRoles: ApiRunFunc
  searchKeyMessages: ApiRunFunc
  searchTiers: ApiRunFunc
  searchSpokespersons: ApiRunFunc
  searchThemeGroups: ApiRunFunc
  searchThemes: ApiRunFunc
  handleProfileSearch: (query: string) => void

  selectedMentionIds: Set<string>
  selectAllVisible: boolean
  toggleMentionSelection: (postID: string, brandID: string) => void
  toggleSelectAllVisible: () => void
  clearSelection: () => void

  batchDeleteMentions: () => Promise<void>
  deleteAllMentions: () => Promise<void>
  mentionsIdList: string[] | undefined
  isDeletingBatch: boolean

  keyMessagesByPostID: Map<string, any[]>
  loadKeyMessagesForMentions: (postIDs: string[], brandID: string) => void
  isLoadingKeyMessages: boolean
  addKeyMessageToMention: (postID: string, messageOrder: number, brandID: string) => Promise<void>
  removeKeyMessageFromMention: (postID: string, messageOrder: number, brandID: string) => Promise<void>
  updateMentionPolarity: (postID: string, brandID: string, newPolarity: string) => void
  removeMentionTag: (mention: MentionResponse, tag: RemovableMentionTag) => void
  isTagPending: (mention: MentionResponse, tag: RemovableMentionTag) => boolean
  setMentionReviewed: (mention: MentionResponse, reviewed: boolean) => void
  isReviewPending: (mention: MentionResponse) => boolean
  expandedId: string | null
  setExpandedId: React.Dispatch<React.SetStateAction<string | null>>
}

const MentionsContext = createContext<MentionsContextOutput>({} as MentionsContextOutput)

export interface DateBinder {
  value: Date | null
  onChange: (date: Date | null) => void
  stringValue: string
}

export function MentionsContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const { companyID, queryParams, navigate } = useRouting()
  const [defaultFilters, setDefaultFilters] = useState<DefaultFilters | undefined>()
  const [currentMention, setCurrentMention] = useState<CurrMention | undefined>()
  const [mentionModalType, setMentionModalType] = useState<MentionModalType>(null)
  const [images, setImages] = useState<Map<string, string>>(new Map())
  const [keyMessagesByPostID, setKeyMessagesByPostID] = useState<Map<string, any[]>>(new Map())
  const [roles, setRoles] = useState<SearchResponse[] | undefined>()
  const [keyMessages, setKeyMessages] = useState<KeyMessageResponse[]>([])
  const [tiers, setTiers] = useState<SearchResponse[] | undefined>()
  const [localMentions, setLocalMentions] = useState<Paged<MentionResponse> | undefined>()
  const [mentionsIdList, setMentionsIdList] = useState<string[] | undefined>()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { sendAlert } = Alert.Hook()

  const [currentPageSize, setCurrentPageSize] = useState(50)
  const [currentSort, setCurrentSort] = useState<{ key: string; direction: 'ASC' | 'DESC' }>({
    key: 'date',
    direction: 'DESC',
  })

  const currentPageFromUrl: number = Number(queryParams.pagina ?? '1')
  const currentPage: number = Math.max(0, currentPageFromUrl - 1)
  const [, , today] = getMonthDays(new Date())
  const FILTERS_STORAGE_KEY = `mentions-filters-${companyID}`

  const polarities = Object.values(PolarityValue)
    .map(
      (value: PolarityValue): SearchResponse => ({
        text: value,
        value: value as string,
      }),
    )
    .sort((a, b) => a.text.localeCompare(b.text))

  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const formatDate = (date: Date | null): string => {
    return date && isValid(date) ? format(date, 'dd/MM/yyyy') : ''
  }

  const parseDate = (dateString: string): Date | null => {
    if (!dateString) return null
    try {
      const parsed = parse(dateString, 'dd/MM/yyyy', new Date())
      return isValid(parsed) ? parsed : null
    } catch {
      return null
    }
  }

  const handleStartDateChange = (date: Date | null) => setStartDate(date)
  const handleEndDateChange = (date: Date | null) => setEndDate(date)

  const startDateBinder: DateBinder = {
    value: startDate,
    onChange: handleStartDateChange,
    stringValue: formatDate(startDate),
  }

  const endDateBinder: DateBinder = {
    value: endDate,
    onChange: handleEndDateChange,
    stringValue: formatDate(endDate),
  }

  const { binder: startTimeBinder, value: startTime, reset: changeStartTime } = useTimeInput()
  const { binder: endTimeBinder, value: endTime, reset: changeEndTime } = useTimeInput()
  const { binder: contentBinder, value: content, reset: changeContent } = useInput()
  const {
    binder: engagementTypeBinder,
    selectedOption: engagementTypeOption,
    reset: changeEngagementType,
  } = Selector.Binder()
  const { binder: engagementMinBinder, value: engagementMin, reset: changeEngagementMin } = useInput()
  const { binder: engagementMaxBinder, value: engagementMax, reset: changeEngagementMax } = useInput()

  const { binder: hasChangesBinder, selectedOption: hasChangesOption, reset: changeHasChanges } = Selector.Binder()
  const {
    binder: hasContentIssuesBinder,
    selectedOption: hasContentIssuesOption,
    reset: hasContentIssues,
  } = Selector.Binder()
  const { binder: reviewedBinder, selectedOption: reviewedOption, reset: changeReviewed } = Selector.Binder()

  const { binder: brandBinder, selectedOptions: selectedBrandItems, reset: changeBrand } = Selector.MultiBinder()
  const { binder: polarityBinder, selectedOptions: selectedPolarities, reset: changePolarity } = Selector.MultiBinder()
  const { binder: channelBinder, selectedOptions: selectedChannels, reset: changeChannel } = Selector.MultiBinder()
  const { binder: roleBinder, selectedOptions: selectedRoles, reset: changeRole } = Selector.MultiBinder()
  const {
    binder: keyMessageBinder,
    selectedOptions: selectedKeyMessages,
    reset: changeKeyMessage,
  } = Selector.MultiBinder()
  const { binder: tierBinder, selectedOptions: selectedTiers, reset: changeTier } = Selector.MultiBinder()
  const { binder: approachBinder, selectedOptions: selectedApproaches, reset: changeApproach } = Selector.MultiBinder()
  const {
    binder: themeGroupBinder,
    selectedOptions: selectedThemeGroups,
    reset: changeThemeGroup,
  } = Selector.MultiBinder()
  const { binder: themeBinder, selectedOptions: selectedThemes, reset: changeTheme } = Selector.MultiBinder()
  const {
    binder: publisherBinder,
    selectedOptions: selectedPublishers,
    reset: changePublisher,
  } = Selector.MultiBinder()
  const { binder: sourceBinder, selectedOptions: selectedSources, reset: changeSource } = Selector.MultiBinder()
  const {
    binder: spokespersonBinder,
    selectedOptions: selectedSpokespersons,
    reset: changeSpokesperson,
  } = Selector.MultiBinder()
  const { binder: profileBinder, selectedOptions: selectedProfiles, reset: changeProfile } = Selector.MultiBinder()

  const [, , company] = Api.Query<CompanyResponse>(`companies/${companyID}`)
  const [searchBrands, isSearchingBrands, brands] = Api.LazyQuery<SearchResponse[]>(
    `brands/search?size=10000&includeCompetitors=true`,
  )
  const [searchChannels, isSearchingChannels, channels] =
    Api.LazyQuery<ChannelsSearchResponse[]>(`channels/search?size=10000`)
  const [searchRoles, isSearchingRoles, rawRoles] = Api.LazyQuery<SearchResponse[]>(`common/roles?size=10000`)
  const [searchKeyMessages, isSearchingKeyMessages, rawKeyMessages] =
    Api.LazyQuery<Paged<KeyMessageResponse>>('key-messages')
  const [searchTiers, isSearchingTiers, rawTiers] = Api.LazyQuery<SearchResponse[]>(`common/tiers?size=10000`)
  const [searchApproaches, isSearchingApproaches, approaches] =
    Api.LazyQuery<SearchResponse[]>(`approaches/search?size=10000`)
  const [searchThemeGroups, isSearchingThemeGroups, themeGroups] =
    Api.LazyQuery<SearchResponse[]>(`theme-groups/search?size=10000`)
  const [searchThemes, isSearchingThemes, themes] = Api.LazyQuery<SearchResponse[]>(`themes/search?size=10000`)
  const [searchPublishers, isSearchingPublishers, publishers] = Api.LazyQuery<SearchResponse[]>(
    `companies/${companyID}/publishers/search?size=10000`,
  )
  const [searchSpokespersons, isSearchingSpokespersons, spokespersons] =
    Api.LazyQuery<SearchResponse[]>('spokespersons/search')
  const [searchProfiles, isSearchingProfiles, profiles] = Api.LazyQuery<SearchResponse[]>(
    `companies/${companyID}/mentions/profiles/search`,
  )

  const {
    isRunning: isLoadingMentions,
    run: runLoadMentions,
    data: mentions,
  } = Api.Command<Paged<MentionResponse>>(Api.Methods.POST, `companies/${companyID}/mentions`)

  const {
    isRunning: isLoadingStats,
    run: runLoadStats,
    data: stats,
  } = Api.Command<MentionsStatsResponse>(Api.Methods.POST, `companies/${companyID}/mentions/stats`)

  const { run: runLoadMentionsAllMentions, data: allMentions } = Api.Command<Paged<MentionResponse>>(
    Api.Methods.POST,
    `companies/${companyID}/mentions`,
  )

  const { isRunning: isExporting, run: getSpreedsheet } = Api.Command<ExportMentionsResponse>(
    Api.Methods.POST,
    `companies/${companyID}/mentions/download`,
  )

  const [reloadImages, , responseImages] = Api.LazyQuery<Image[]>(`files/batch`)

  const [selectedMentionIds, setSelectedMentionIds] = useState<Set<string>>(new Set())
  const [selectAllVisible, setSelectAllVisible] = useState(false)

  const { isRunning: isDeletingBatch, run: batchDeleteMentions } = Api.Command<{ mentionIds: string[] }>(
    Api.Methods.DELETE,
    `companies/:companyID/mentions/batch`,
  )

  const { isRunning: isLoadingKeyMessages, run: loadKeyMessages } = Api.Command<any>(
    Api.Methods.POST,
    `companies/${companyID}/mentions/key-messages`,
  )

  const { run: addKeyMessage } = Api.Command<void>(
    Api.Methods.POST,
    `companies/:companyID/mentions/:mentionID/key-messages/:messageOrder`,
  )

  const { run: removeKeyMessage } = Api.Command<void>(
    Api.Methods.DELETE,
    `companies/:companyID/mentions/:mentionID/key-messages/:messageOrder`,
  )

  // Mesmo endpoint que o modal de edição usa. O `isRunning` do hook é global
  // demais pra isso (várias tags podem estar em voo ao mesmo tempo), então a
  // pendência é controlada por tag em `pendingTagIDs`.
  const { run: patchMentionTag } = Api.Command<UpdateMentionRequest>(
    Api.Methods.PATCH,
    `companies/:companyID/mentions/:mentionID`,
  )

  const [pendingTagIDs, setPendingTagIDs] = useState<Set<string>>(new Set())

  // Endpoint próprio: o PATCH de edição sempre marca a menção como "com
  // alterações", e revisar não é editar. Pendência por linha, como nas tags.
  const { run: patchMentionReviewed } = Api.Command<void>(
    Api.Methods.PATCH,
    `companies/:companyID/mentions/:mentionID/reviewed`,
  )
  const [pendingReviewIDs, setPendingReviewIDs] = useState<Set<string>>(new Set())

  const deletingInProgressDialog = Dialog.Hook({
    title: <DialogTitle>Exclusão em andamento</DialogTitle>,
    content: (
      <div>
        <p style={{ textAlign: 'center' }}>
          Estamos deletando os posts selecionados. Dependendo da quantidade, esse processo pode levar alguns (muitos)
          minutos.
        </p>
        <br />

        <p style={{ textAlign: 'center' }}>
          <strong>Este pop up fechará sozinho quando o processo terminar.</strong> Mas fique à vontade para continuar
          usando o site normalmente em outra guia enquanto isso.
        </p>
      </div>
    ),
    size: 'default',
    customActions: <></>,
  })

  useEffect(() => {
    if (mentions) {
      setLocalMentions(mentions)
    }
  }, [mentions])

  useEffect(() => {
    isDeletingBatch ? deletingInProgressDialog.open() : deletingInProgressDialog.close()
  }, [isDeletingBatch])

  useEffect(() => {
    // allMentions.total é a contagem real do filtro que gerou esta resposta
    // (ao contrário de mentions?.total, que em getMentiosIdsList ainda reflete
    // o filtro anterior no momento da checagem, por ser assíncrono). É essa
    // contagem fresca que decide se a lista é populada, não a checagem prévia.
    if (!allMentions || (allMentions.total ?? 0) > DELETE_ALL_MAX_MENTIONS) {
      setMentionsIdList([])
      return
    }

    setMentionsIdList(allMentions?.elements.map((mention) => `${mention.postID}_${mention.brandID}`))
  }, [allMentions])

  useEffect(() => {
    if (!company) return
    if (!company?.healthCheck?.isHealthy) {
      sendAlert({
        type: 'error',
        title: 'Erro no manual',
        message: 'Empresa está com manual incompleto: Favor revisar o manual!',
      })
      navigate(Routes.CompanyDetails.path, companyID!)
    }
  }, [company])

  useEffect(() => {
    if (!rawRoles) return

    const filteredRoles = rawRoles.filter((role) => {
      return !role.text?.includes('SEM REGRA')
    })

    setRoles(filteredRoles)
  }, [rawRoles])

  useEffect(() => {
    if (!rawTiers) return

    const filteredTiers = rawTiers.filter((tier) => {
      return !tier.text?.includes('SEM REGRA')
    })

    setTiers(filteredTiers)
  }, [rawTiers])

  useEffect(() => {
    if (rawKeyMessages) {
      setKeyMessages(rawKeyMessages.elements || [])
    }
  }, [rawKeyMessages])

  function resetFilters(): void {
    if (defaultFilters) {
      const start = parseDate(defaultFilters.startDate)
      const end = parseDate(defaultFilters.endDate)

      setStartDate(start)
      setEndDate(end)
      changeBrand(defaultFilters.brands)
      changeThemeGroup([])
    }
    changeApproach([])
    changeChannel([])
    changeContent(undefined)
    changePolarity([])
    changeProfile([])
    changePublisher([])
    changeSource([])
    changeSpokesperson([])
    changeRole([])
    changeKeyMessage([])
    changeTier([])
    changeTheme([])
    changeStartTime(undefined)
    changeEndTime(undefined)
    changeEngagementType(undefined)
    changeEngagementMin(undefined)
    changeEngagementMax(undefined)
    changeHasChanges(undefined)
    hasContentIssues(undefined)
    changeReviewed(undefined)

    try {
      localStorage.removeItem(FILTERS_STORAGE_KEY)
    } catch (error) {
      console.error('Failed to clear filters from localStorage:', error)
    }
  }

  function saveFiltersToStorage(): void {
    try {
      const filtersToSave = {
        startDate: startDateBinder.stringValue,
        endDate: endDateBinder.stringValue,
        selectedBrands: selectedBrandItems,
        selectedThemeGroups,
        selectedApproaches,
        selectedChannels,
        selectedPolarities,
        selectedProfiles,
        selectedPublishers,
        selectedRoles,
        selectedKeyMessages,
        selectedTiers,
        selectedSources,
        selectedSpokespersons,
        selectedThemes,
        excludedApproaches: approachBinder.excludedOptions,
        excludedChannels: channelBinder.excludedOptions,
        excludedPolarities: polarityBinder.excludedOptions,
        excludedProfiles: profileBinder.excludedOptions,
        excludedPublishers: publisherBinder.excludedOptions,
        excludedRoles: roleBinder.excludedOptions,
        excludedKeyMessage: keyMessageBinder.excludedOptions,
        excludedTiers: tierBinder.excludedOptions,
        excludedSources: sourceBinder.excludedOptions,
        excludedSpokespersons: spokespersonBinder.excludedOptions,
        excludedThemes: themeBinder.excludedOptions,
        excludedThemeGroups: themeGroupBinder.excludedOptions,
        content,
        startTime,
        endTime,
        engagementType: engagementTypeOption?.value,
        engagementMin,
        engagementMax,
        hasChanges: hasChangesOption?.value,
        hasContentIssues: hasContentIssuesOption?.value,
        reviewed: reviewedOption?.value,
        pageSize: currentPageSize,
        sort: currentSort,
      }

      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filtersToSave))
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error)
    }
  }

  function restoreFiltersFromStorage(): void {
    try {
      const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY)
      if (!savedFilters) return

      const filters = JSON.parse(savedFilters)

      if (filters.startDate) {
        const start = parseDate(filters.startDate)
        if (start) setStartDate(start)
      }
      if (filters.endDate) {
        const end = parseDate(filters.endDate)
        if (end) setEndDate(end)
      }

      if (filters.selectedBrands) changeBrand(filters.selectedBrands)
      if (filters.selectedThemeGroups) changeThemeGroup(filters.selectedThemeGroups)
      if (filters.selectedApproaches) changeApproach(filters.selectedApproaches)
      if (filters.selectedChannels) changeChannel(filters.selectedChannels)
      if (filters.selectedPolarities) changePolarity(filters.selectedPolarities)
      if (filters.selectedProfiles) changeProfile(filters.selectedProfiles)
      if (filters.selectedPublishers) changePublisher(filters.selectedPublishers)
      if (filters.selectedRoles) changeRole(filters.selectedRoles)
      if (filters.selectedKeyMessage) changeKeyMessage(filters.selectedKeyMessage)
      if (filters.selectedTiers) changeTier(filters.selectedTiers)
      if (filters.selectedSources) changeSource(filters.selectedSources)
      if (filters.selectedSpokespersons) changeSpokesperson(filters.selectedSpokespersons)
      if (filters.selectedThemes) changeTheme(filters.selectedThemes)
      if (filters.content) changeContent(filters.content)
      if (filters.startTime) changeStartTime(filters.startTime)
      if (filters.endTime) changeEndTime(filters.endTime)
      if (filters.engagementType) changeEngagementType({ text: filters.engagementType, value: filters.engagementType })
      if (filters.engagementMin) changeEngagementMin(filters.engagementMin)
      if (filters.engagementMax) changeEngagementMax(filters.engagementMax)
      if (filters.hasChanges) changeHasChanges({ text: filters.hasChanges, value: filters.hasChanges })
      if (filters.hasContentIssues)
        hasContentIssues({ text: filters.hasContentIssues, value: filters.hasContentIssues })
      if (filters.reviewed) changeReviewed(REVIEWED_OPTIONS.find((option) => option.value === filters.reviewed))
      if (filters.pageSize) setCurrentPageSize(filters.pageSize)
      if (filters.sort) setCurrentSort(filters.sort)
    } catch (error) {
      console.error('Failed to restore filters from localStorage:', error)
    }
  }

  const lastStatsFiltersRef = useRef<string | null>(null)
  const lastSelectionFiltersRef = useRef<string | null>(null)

  const loadMentions = (requestBody: any) => {
    runLoadMentions({ input: requestBody })

    const { page: _page, size: _size, orderBy: _orderBy, ...filtersOnly } = requestBody
    const filtersSignature = JSON.stringify(filtersOnly)
    if (filtersSignature !== lastStatsFiltersRef.current) {
      lastStatsFiltersRef.current = filtersSignature
      runLoadStats({ input: requestBody })
    }

    // A seleção guarda IDs compostos (postID_brandID) que continuam válidos ao
    // paginar, mas viram lixo invisível quando o filtro muda: as menções somem
    // da tela e o batch acaba mandando pro back menções de marcas/fontes que a
    // pessoa nem enxerga mais. Por isso zeramos só quando o filtro em si muda
    // (page/size/orderBy ficam de fora da assinatura acima).
    if (filtersSignature !== lastSelectionFiltersRef.current) {
      const isFirstLoad = lastSelectionFiltersRef.current === null
      lastSelectionFiltersRef.current = filtersSignature
      if (!isFirstLoad) {
        setSelectedMentionIds(new Set())
        setSelectAllVisible(false)
      }
    }
  }

  function applyFilters(): void {
    const startDateStr = startDateBinder.stringValue
    const endDateStr = endDateBinder.stringValue
    const orderBy = `${currentSort.key}:${currentSort.direction}`

    const baseFilters = createBaseFilters(
      selectedBrandItems,
      selectedThemeGroups,
      startDateStr,
      endDateStr,
      currentPage,
      currentPageSize,
      orderBy,
    )

    const requestBody = addFilters(baseFilters, {
      selectedApproaches,
      selectedChannels,
      selectedPolarities,
      selectedProfiles,
      selectedPublishers,
      selectedRoles,
      selectedKeyMessages,
      selectedTiers,
      selectedSources,
      selectedSpokespersons,
      selectedThemes,
      selectedThemeGroups,
      excludedApproaches: approachBinder.excludedOptions,
      excludedChannels: channelBinder.excludedOptions,
      excludedPolarities: polarityBinder.excludedOptions,
      excludedProfiles: profileBinder.excludedOptions,
      excludedPublishers: publisherBinder.excludedOptions,
      excludedRoles: roleBinder.excludedOptions,
      excludedKeyMessages: keyMessageBinder.excludedOptions,
      excludedTiers: tierBinder.excludedOptions,
      excludedSources: sourceBinder.excludedOptions,
      excludedSpokespersons: spokespersonBinder.excludedOptions,
      excludedThemes: themeBinder.excludedOptions,
      excludedThemeGroups: themeGroupBinder.excludedOptions,
      content,
      startTime,
      endTime,
      engagementType: engagementTypeOption?.value as string,
      engagementMin,
      engagementMax,
      hasChanges: hasChangesOption?.value ? hasChangesOption.value === 'COM' : undefined,
      hasContentIssues: hasContentIssuesOption?.value ? hasContentIssuesOption.value === 'COM' : undefined,
      reviewed: reviewedOption?.value ? reviewedOption.value === 'COM' : undefined,
    })

    loadMentions(requestBody)
    saveFiltersToStorage()
    getMentiosIdsList()
  }

  function getMentiosIdsList(): void {
    // Otimização apenas: evita disparar a busca completa quando o total do
    // filtro anterior já era grande. mentions?.total ainda pode estar
    // desatualizado aqui (loadMentions é assíncrono), então isso NÃO é o que
    // impede uma lista grande de virar mentionsIdList — quem faz isso é a
    // checagem por allMentions.total (contagem fresca) no useEffect acima.
    if ((mentions?.total ?? 0) > DELETE_ALL_MAX_MENTIONS) return

    const startDateStr = startDateBinder.stringValue
    const endDateStr = endDateBinder.stringValue
    const orderBy = `${currentSort.key}:${currentSort.direction}`

    const baseFilters = createBaseFilters(
      selectedBrandItems,
      selectedThemeGroups,
      startDateStr,
      endDateStr,
      0,
      99999999999,
      orderBy,
    )

    const requestBody = addFilters(baseFilters, {
      selectedApproaches,
      selectedChannels,
      selectedPolarities,
      selectedProfiles,
      selectedPublishers,
      selectedRoles,
      selectedKeyMessages,
      selectedTiers,
      selectedSources,
      selectedSpokespersons,
      selectedThemes,
      selectedThemeGroups,
      excludedApproaches: approachBinder.excludedOptions,
      excludedChannels: channelBinder.excludedOptions,
      excludedPolarities: polarityBinder.excludedOptions,
      excludedProfiles: profileBinder.excludedOptions,
      excludedPublishers: publisherBinder.excludedOptions,
      excludedRoles: roleBinder.excludedOptions,
      excludedKeyMessages: keyMessageBinder.excludedOptions,
      excludedTiers: tierBinder.excludedOptions,
      excludedSources: sourceBinder.excludedOptions,
      excludedSpokespersons: spokespersonBinder.excludedOptions,
      excludedThemes: themeBinder.excludedOptions,
      excludedThemeGroups: themeGroupBinder.excludedOptions,
      content,
      startTime,
      endTime,
      engagementType: engagementTypeOption?.value as string,
      engagementMin,
      engagementMax,
      hasChanges: hasChangesOption?.value ? hasChangesOption.value === 'COM' : undefined,
      hasContentIssues: hasContentIssuesOption?.value ? hasContentIssuesOption.value === 'COM' : undefined,
      reviewed: reviewedOption?.value ? reviewedOption.value === 'COM' : undefined,
    })

    runLoadMentionsAllMentions({ input: requestBody })
  }

  async function downloadSpreadsheet(): Promise<ExportMentionsResponse> {
    try {
      const startDateStr = startDateBinder.stringValue
      const endDateStr = endDateBinder.stringValue

      const baseFilters = createBaseFilters(selectedBrandItems, selectedThemeGroups, startDateStr, endDateStr)

      const requestBody = addFilters(baseFilters, {
        selectedApproaches,
        selectedChannels,
        selectedPolarities,
        selectedProfiles,
        selectedPublishers,
        selectedRoles,
        selectedKeyMessages,
        selectedTiers,
        selectedSources,
        selectedSpokespersons,
        selectedThemes,
        selectedThemeGroups,
        excludedApproaches: approachBinder.excludedOptions,
        excludedChannels: channelBinder.excludedOptions,
        excludedPolarities: polarityBinder.excludedOptions,
        excludedProfiles: profileBinder.excludedOptions,
        excludedPublishers: publisherBinder.excludedOptions,
        excludedRoles: roleBinder.excludedOptions,
        excludedKeyMessages: keyMessageBinder.excludedOptions,
        excludedTiers: tierBinder.excludedOptions,
        excludedSources: sourceBinder.excludedOptions,
        excludedSpokespersons: spokespersonBinder.excludedOptions,
        excludedThemes: themeBinder.excludedOptions,
        excludedThemeGroups: themeGroupBinder.excludedOptions,
        content,
        startTime,
        endTime,
        engagementType: engagementTypeOption?.value as string,
        engagementMin,
        engagementMax,
        hasChanges: hasChangesOption?.value ? hasChangesOption.value === 'COM' : undefined,
        hasContentIssues: hasContentIssuesOption?.value ? hasContentIssuesOption.value === 'COM' : undefined,
        reviewed: reviewedOption?.value ? reviewedOption.value === 'COM' : undefined,
      })

      return new Promise((resolve, reject) => {
        getSpreedsheet({
          input: requestBody,
          onSuccess: (data) => {
            resolve({
              message: data.message,
              status: data.status,
            })
          },
          onFail: (error) => {
            reject(error)
          },
        })
      })
    } catch (error) {
      return {
        message: error instanceof Error ? error.message : String(error),
        status: 500,
      }
    }
  }

  const toggleMentionSelection = (postID: string, brandID: string) => {
    const compositeId = `${postID}_${brandID}`

    setSelectedMentionIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(compositeId)) {
        newSet.delete(compositeId)
      } else {
        newSet.add(compositeId)
      }
      return newSet
    })
  }

  const toggleSelectAllVisible = () => {
    if (selectAllVisible) {
      setSelectedMentionIds(new Set())
    } else {
      const visibleCompositeIds = localMentions?.elements.map((mention) => `${mention.postID}_${mention.brandID}`) || []
      const uniqueIds = Array.from(new Set(visibleCompositeIds))
      setSelectedMentionIds(new Set(uniqueIds))
    }
    setSelectAllVisible(!selectAllVisible)
  }

  const clearSelection = () => {
    setSelectedMentionIds(new Set())
    setSelectAllVisible(false)
  }

  // Patch local (lista + menção aberta no detalhe) sem mexer em hasChanges.
  // Base do applyMentionPatch (edições, que marcam hasChanges) e do toggle de
  // revisão (que não é edição e não pode ligar o ícone de "com alterações").
  const patchLocalMention = useCallback((postID: string, brandID: string, patch: Partial<MentionResponse>) => {
    setLocalMentions((prev) => {
      if (!prev?.elements) return prev

      const updatedElements = prev.elements.map((mention) =>
        mention.postID === postID && mention.brandID === brandID ? { ...mention, ...patch } : mention,
      )

      return { ...prev, elements: updatedElements }
    })

    setCurrentMention((prev) =>
      prev?.val.postID === postID && prev.val.brandID === brandID ? { ...prev, val: { ...prev.val, ...patch } } : prev,
    )
  }, [])

  const applyMentionPatch = useCallback(
    (postID: string, brandID: string, patch: Partial<MentionResponse>) =>
      patchLocalMention(postID, brandID, { ...patch, hasChanges: true }),
    [patchLocalMention],
  )

  const updateMentionPolarity = useCallback(
    (postID: string, brandID: string, newPolarity: string) =>
      applyMentionPatch(postID, brandID, { polarity: newPolarity }),
    [applyMentionPatch],
  )

  const setTagPending = useCallback((key: string, pending: boolean): void => {
    setPendingTagIDs((prev) => {
      const next = new Set(prev)
      if (pending) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const restoreMentionTag = useCallback(
    (snapshot: MentionResponse, tag: RemovableMentionTag): void => {
      const key = mentionTagKey(snapshot, tag)
      setTagPending(key, true)

      patchMentionTag({
        input: buildTagAddition(snapshot, tag),
        binds: [
          ['companyID', snapshot.companyID],
          ['mentionID', snapshot.postID],
        ],
        onSuccess: () => {
          setTagPending(key, false)
          applyMentionPatch(snapshot.postID, snapshot.brandID, withTagRestored(snapshot, tag))
          sendAlert({
            type: 'success',
            title: TAG_COPY[tag.kind].restored,
            message: `"${tag.name}" voltou para esta menção.`,
          })
        },
        onFail: (error) => {
          setTagPending(key, false)
          sendAlert({
            type: 'error',
            title: 'Erro ao restaurar',
            message: `Não foi possível restaurar "${tag.name}": ${error.message}`,
          })
        },
      })
    },
    [patchMentionTag, applyMentionPatch, setTagPending, sendAlert],
  )

  const removeMentionTag = useCallback(
    (mention: MentionResponse, tag: RemovableMentionTag): void => {
      const key = mentionTagKey(mention, tag)
      // Snapshot antes de qualquer mutação: é a partir daqui que o desfazer
      // remonta o estado, inclusive o grupo do tema caso ele fique vazio.
      const snapshot = mention

      setTagPending(key, true)

      patchMentionTag({
        input: buildTagDeletion(mention, tag),
        binds: [
          ['companyID', mention.companyID],
          ['mentionID', mention.postID],
        ],
        onSuccess: () => {
          setTagPending(key, false)
          applyMentionPatch(mention.postID, mention.brandID, withoutTag(snapshot, tag))
          sendAlert(
            {
              type: 'success',
              title: TAG_COPY[tag.kind].removed,
              message: `"${tag.name}" saiu desta menção.`,
              action: { label: 'Desfazer', onClick: () => restoreMentionTag(snapshot, tag) },
            },
            { ttl: UNDO_ALERT_TTL },
          )
        },
        onFail: (error) => {
          setTagPending(key, false)
          sendAlert({
            type: 'error',
            title: TAG_COPY[tag.kind].error,
            message: `Não foi possível remover "${tag.name}": ${error.message}`,
          })
        },
      })
    },
    [patchMentionTag, applyMentionPatch, setTagPending, sendAlert, restoreMentionTag],
  )

  const isTagPending = useCallback(
    (mention: MentionResponse, tag: RemovableMentionTag): boolean => pendingTagIDs.has(mentionTagKey(mention, tag)),
    [pendingTagIDs],
  )

  const mentionReviewKey = (mention: MentionResponse): string => `${mention.postID}_${mention.brandID}`

  const setReviewPending = useCallback((key: string, pending: boolean): void => {
    setPendingReviewIDs((prev) => {
      const next = new Set(prev)
      if (pending) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const isReviewPending = useCallback(
    (mention: MentionResponse): boolean => pendingReviewIDs.has(mentionReviewKey(mention)),
    [pendingReviewIDs],
  )

  // Toggle otimista com reversão em falha. Não chama `abort`: o hook não passa
  // o signal pro axios, então abortar não cancela nada. Com o filtro "Sem
  // revisão" ativo a linha marcada continua visível até o próximo "Filtrar",
  // igual ao que acontece ao remover uma tag.
  const setMentionReviewed = useCallback(
    (mention: MentionResponse, reviewed: boolean): void => {
      const key = mentionReviewKey(mention)
      const previous = mention.reviewed ?? false
      if (pendingReviewIDs.has(key) || previous === reviewed) return

      setReviewPending(key, true)
      patchLocalMention(mention.postID, mention.brandID, { reviewed })

      patchMentionReviewed<SetMentionReviewedRequest>({
        input: { brandID: mention.brandID, reviewed },
        binds: [
          ['companyID', mention.companyID],
          ['mentionID', mention.postID],
        ],
        onSuccess: () => {
          setReviewPending(key, false)
          // Reaplica: um refetch (página, ordenação, edição) pode ter
          // sobrescrito localMentions enquanto o PATCH estava em voo.
          patchLocalMention(mention.postID, mention.brandID, { reviewed })
        },
        onFail: (error) => {
          setReviewPending(key, false)
          patchLocalMention(mention.postID, mention.brandID, { reviewed: previous })
          sendAlert({
            type: 'error',
            title: 'Erro ao marcar revisão',
            message: error?.message ?? 'Não foi possível atualizar a revisão da menção.',
          })
        },
      })
    },
    [pendingReviewIDs, setReviewPending, patchLocalMention, patchMentionReviewed, sendAlert],
  )

  const handleBatchDelete = async (): Promise<void> => {
    if (selectedMentionIds.size === 0) return

    try {
      const mentionIdsForBackend = Array.from(selectedMentionIds)
      const firstSelectedMention = localMentions?.elements.find((mention) =>
        selectedMentionIds.has(`${mention.postID}_${mention.brandID}`),
      )

      if (!firstSelectedMention) {
        sendAlert({
          type: 'error',
          title: 'Erro ao excluir menções',
          message: 'Não foi possível identificar as menções selecionadas.',
        })
        return
      }

      batchDeleteMentions({
        input: {
          mentionIds: mentionIdsForBackend,
        },
        binds: [['companyID', firstSelectedMention.companyID]],
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: 'Menções excluídas',
            message: `${selectedMentionIds.size} menções foram excluídas com sucesso.`,
          })

          clearSelection()
          applyFilters()
        },
        onFail: (error) => {
          sendAlert({
            type: 'error',
            title: 'Erro ao excluir menções',
            message: `Não foi possível excluir as menções selecionadas: ${error.message}`,
          })
        },
      })
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao excluir menções',
        message: 'Não foi possível excluir as menções selecionadas.',
      })
    }
  }

  const handleDeleteAll = async (): Promise<void> => {
    if (!mentionsIdList || mentionsIdList?.length === 0) return

    try {
      batchDeleteMentions({
        input: {
          mentionIds: mentionsIdList,
        },
        binds: [['companyID', String(companyID)]],
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: 'Menções excluídas',
            message: `${mentions?.total} menções foram excluídas com sucesso.`,
          })

          clearSelection()
          applyFilters()
        },
        onFail: (error) => {
          sendAlert({
            type: 'error',
            title: 'Erro ao excluir menções',
            message: `Não foi possível excluir as menções selecionadas: ${error.message}`,
          })
        },
      })
    } catch (error) {
      sendAlert({
        type: 'error',
        title: 'Erro ao excluir menções',
        message: 'Não foi possível excluir as menções selecionadas.',
      })
    }
  }

  const loadKeyMessagesForMentions = useCallback(
    (postIDs: string[], brandID: string) => {
      if (postIDs.length === 0) return

      loadKeyMessages({
        input: { postIDs, brandIDs: brandID },
        onSuccess: (data) => {
          const newMap = new Map<string, any[]>()
          Object.entries(data).forEach(([postID, keyMessages]) => {
            newMap.set(postID, keyMessages as any[])
          })
          setKeyMessagesByPostID(newMap)
        },
        onFail: () => {
          sendAlert({
            type: 'error',
            title: 'Erro ao carregar mensagens-chave',
            message: 'Não foi possível carregar as mensagens-chave das menções.',
          })
        },
      })
    },
    [loadKeyMessages],
  )

  const handleAddKeyMessageToMention = useCallback(
    async (postID: string, messageOrder: number, brandID: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        addKeyMessage({
          binds: [
            ['companyID', companyID!],
            ['mentionID', postID],
            ['messageOrder', messageOrder.toString()],
          ],
          onSuccess: () => {
            sendAlert({
              type: 'success',
              title: 'Mensagem-chave adicionada',
              message: 'A mensagem-chave foi associada à menção com sucesso.',
            })

            loadKeyMessagesForMentions([postID], brandID)
            resolve()
          },
          onFail: (error) => {
            sendAlert({
              type: 'error',
              title: 'Erro ao adicionar mensagem-chave',
              message: `Não foi possível associar a mensagem-chave: ${error.message}`,
            })
            reject(error)
          },
        })
      })
    },
    [addKeyMessage, companyID, loadKeyMessagesForMentions],
  )

  const handleRemoveKeyMessageFromMention = useCallback(
    async (postID: string, messageOrder: number, brandID: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        removeKeyMessage({
          binds: [
            ['companyID', companyID!],
            ['mentionID', postID],
            ['messageOrder', messageOrder.toString()],
          ],
          onSuccess: () => {
            sendAlert({
              type: 'success',
              title: 'Mensagem-chave removida',
              message: 'A mensagem-chave foi desassociada da menção com sucesso.',
            })

            loadKeyMessagesForMentions([postID], brandID)
            resolve()
          },
          onFail: (error) => {
            sendAlert({
              type: 'error',
              title: 'Erro ao remover mensagem-chave',
              message: `Não foi possível desassociar a mensagem-chave: ${error.message}`,
            })
            reject(error)
          },
        })
      })
    },
    [removeKeyMessage, companyID, loadKeyMessagesForMentions],
  )

  const [profileQuery, setProfileQuery] = useState('')
  const [profileOptions, setProfileOptions] = useState<SearchResponse[]>([])

  useEffect(() => {
    if (profileQuery.length < 3) {
      setProfileOptions([])
      return
    }

    const timeout = setTimeout(() => {
      searchProfiles({ company: companyID, name: profileQuery })
    }, 300)

    return () => clearTimeout(timeout)
  }, [profileQuery])

  useEffect(() => {
    if (profiles) setProfileOptions(profiles)
  }, [profiles])

  const handleProfileSearch = (query: string) => {
    setProfileQuery(query)
  }

  useEffect((): void => {
    if (!responseImages) return

    const newImages = new Map<string, string>()
    responseImages.forEach((img) => {
      if (img.id) {
        newImages.set(img.id, atob(img.content))
      }
    })
    setImages(newImages)
  }, [responseImages])

  useEffect((): void => {
    if (!localMentions || !localMentions.elements.length) return

    const ids = localMentions?.elements?.map((el) => [el.brandLogo, el.channelLogoID]).flat()
    const filteredIds = ids.filter(Boolean)
    if (filteredIds.length > 0) {
      reloadImages({
        ids: filteredIds.join(','),
      })
    }
  }, [localMentions])

  useEffect((): void => {
    if (!companyID) return
    const brandsList = selectedBrandItems.map((b) => String(b.value))

    searchBrands({
      company: companyID,
      orderBy: 'internalID:ASC',
    })

    searchChannels({
      company: companyID,
      orderBy: 'value:ASC',
      isActive: 'true',
    })
    searchRoles()
    searchKeyMessages({
      company: companyID,
      brandIDs: brandsList,
    })
    searchTiers()
    searchApproaches()

    searchPublishers({
      company: companyID,
      onlyActive: 'true',
    })
    searchSpokespersons({
      company: companyID,
    })
  }, [])

  useEffect(() => {
    const brandsList = selectedBrandItems.filter((b) => b.text.includes('(C)')).map((b) => String(b.value))

    searchThemeGroups({
      company: companyID,
      brandIDs: brandsList,
      orderBy: 'internalID:ASC',
    })
  }, [selectedBrandItems])

  useEffect(() => {
    if (defaultFilters) {
      applyFilters()
    }
  }, [currentPage, currentSort])

  useEffect(() => {
    if (defaultFilters && currentPageFromUrl === 1) {
      applyFilters()
    }
  }, [currentPageSize])

  useEffect(() => {
    if (!defaultFilters) return

    const start = parseDate(defaultFilters.startDate)
    const end = parseDate(defaultFilters.endDate)

    setStartDate(start)
    setEndDate(end)
    changeBrand(defaultFilters.brands)
    changeThemeGroup([])
  }, [defaultFilters])

  useEffect(() => {
    if (!brands?.length || defaultFilters) return

    const mainCompanyBrands = brands
      .filter((brand) => !brand.text.endsWith(' (C)'))
      .map((brand) => ({
        text: Str.from(brand.text).initCap,
        value: brand.value,
      }))

    setDefaultFilters({
      startDate: today,
      endDate: today,
      brands: mainCompanyBrands,
      themeGroups: [],
    })
  }, [brands])

  useEffect(() => {
    if (defaultFilters) {
      restoreFiltersFromStorage()
    }
  }, [defaultFilters])

  useEffect(() => {
    const activeBrands = selectedBrandItems.length ? selectedBrandItems : defaultFilters?.brands || []
    const hasCompetitorBrands = activeBrands.some((brand) => brand.text.endsWith(' (C)'))

    if (!companyID || !activeBrands.length || !selectedThemeGroups.length) return

    searchThemes({
      company: companyID,
      marca: activeBrands.map((b) => b.value).join(','),
      grupoTematico: selectedThemeGroups.map((b) => b.value).join(','),
      fathersOnly: !hasCompetitorBrands,
      orderBy: 'value:ASC',
      isActive: 'true',
    })
  }, [companyID, selectedBrandItems, selectedThemeGroups, defaultFilters?.brands])

  useEffect(() => {
    if (!localMentions?.elements.length) {
      setSelectAllVisible(false)
      return
    }

    const visibleCompositeIds = localMentions.elements.map((mention) => `${mention.postID}_${mention.brandID}`)
    const uniqueVisibleIds = Array.from(new Set(visibleCompositeIds))
    const allSelected = uniqueVisibleIds.every((compositeId) => selectedMentionIds.has(compositeId))
    setSelectAllVisible(allSelected)
  }, [selectedMentionIds, localMentions])

  useEffect(() => {
    if (!localMentions?.elements.length) return

    const newMap = new Map<string, any[]>()
    localMentions.elements.forEach((mention) => {
      if (mention.keyMessages && mention.keyMessages.length > 0) {
        newMap.set(mention.postID, mention.keyMessages)
      }
    })

    setKeyMessagesByPostID((prev) => {
      const merged = new Map(prev)
      newMap.forEach((value, key) => {
        merged.set(key, value)
      })
      return merged
    })
  }, [localMentions?.elements])

  return (
    <MentionsContext.Provider
      value={{
        defaultFilters,
        currentMention,
        mentionModalType,
        currentPageSize,
        currentSort,
        setCurrentPageSize,
        setCurrentSort,
        selectMention: (mention, modalType = 'edit') => {
          setCurrentMention(mention)
          setMentionModalType(mention ? modalType : null)
        },

        applyFilters,
        resetFilters,
        downloadSpreadsheet,

        startDateBinder,
        endDateBinder,
        startTimeBinder,
        endTimeBinder,
        brandBinder,
        polarityBinder,
        channelBinder,
        roleBinder,
        keyMessageBinder,
        tierBinder,
        approachBinder,
        themeGroupBinder,
        themeBinder,
        publisherBinder,
        sourceBinder,
        contentBinder,
        hasChangesBinder,
        engagementTypeBinder,
        engagementMinBinder,
        engagementMaxBinder,
        spokespersonBinder,
        profileBinder,

        isSearchingBrands,
        isSearchingChannels,
        isSearchingRoles,
        isSearchingKeyMessages,
        isSearchingTiers,
        isSearchingApproaches,
        isSearchingThemeGroups,
        isSearchingThemes,
        isSearchingPublishers,
        isSearchingSpokespersons,
        isSearchingProfiles,
        isLoadingMentions,
        isLoadingStats,
        isExporting,
        brands,
        polarities,
        channels,
        roles,
        keyMessages,
        tiers,
        approaches,
        themeGroups,
        themes,
        publishers,
        spokespersons,
        profiles: profileOptions,
        mentions: localMentions,
        stats,
        images,
        searchBrands,
        searchChannels,
        searchRoles,
        searchKeyMessages,
        searchTiers,
        searchApproaches,
        searchThemeGroups,
        searchThemes,
        searchPublishers,
        searchSpokespersons,
        searchProfiles,
        loadMentions,
        handleProfileSearch,

        selectedMentionIds,
        selectAllVisible,
        toggleMentionSelection,
        toggleSelectAllVisible,
        clearSelection,
        batchDeleteMentions: handleBatchDelete,
        deleteAllMentions: handleDeleteAll,
        mentionsIdList,

        isDeletingBatch,

        hasContentIssuesBinder,
        reviewedBinder,

        keyMessagesByPostID,
        loadKeyMessagesForMentions,
        isLoadingKeyMessages,
        addKeyMessageToMention: handleAddKeyMessageToMention,
        removeKeyMessageFromMention: handleRemoveKeyMessageFromMention,
        updateMentionPolarity,
        removeMentionTag,
        isTagPending,
        setMentionReviewed,
        isReviewPending,
        expandedId,
        setExpandedId,
      }}>
      {children}
    </MentionsContext.Provider>
  )
}

export function useMentionsContext(): MentionsContextOutput {
  const context = useContext(MentionsContext)
  if (!context) throw new Error('useMentionsContext deve ser chamado dentro do contexto correto')
  return context
}
